import { useReviewerSchema } from "../../../server/auth/use-reviewer-schema";
import { useSupabaseCallback, useSupabaseLoader } from "../../../server/supabase/hooks";

import type { SupabaseHandlerArg } from "../../../server/supabase/hooks/types";
import type { UUID } from "../../../utils/uuid";

export interface CancelledReview {
  moment_id: UUID;
  title: string;
  cancelledBy: string;
}

/**
 * Cancelling a review takes the Communication away with it,
 * so a reviewer never sees the step that did it: the email and this notice is the
 * only way they hear of it, and putting one away is remembered so it is only ever said once.
 */
export function useCancelledReviews(): {
  cancelled: CancelledReview[];
  dismiss: (moment_id: UUID) => Promise<void>;
} {
  const reviewerSchema = useReviewerSchema();

  const { data, reload } = useSupabaseLoader<CancelledReview[], "public" | "reviewer">(
    async ({ supabase, account_id }) => {
      if (!reviewerSchema) return [];

      // oxlint-disable-next-line typescript/no-unsafe-type-assertion
      const reviewerSupabase = supabase as SupabaseHandlerArg<"reviewer">["supabase"];

      const { data: cancelled, error } = await reviewerSupabase.rpc("cancelled_reviews", {
        account_id_: account_id,
      });

      if (error) throw error;

      return (cancelled ?? []).map(({ moment_id, title, cancelled_by }) => ({
        moment_id,
        title,
        cancelledBy: cancelled_by,
      }));
    },
    [reviewerSchema],
    reviewerSchema ? "reviewer" : "public",
  );

  const putAway = useSupabaseCallback<[UUID], void, "reviewer">(
    async ({ supabase, account_id }, moment_id) => {
      const { error } = await supabase.rpc("dismiss_cancelled_review", {
        moment_id_: moment_id,
        account_id_: account_id,
      });

      if (error) throw error;
    },
    [],
    "reviewer",
  );

  const dismiss = async (moment_id: UUID): Promise<void> => {
    await putAway(moment_id);
    reload();
  };

  return { cancelled: data ?? [], dismiss };
}
